import { useRef, useState, type CSSProperties, type KeyboardEvent } from "react";

import type { DocumentOut, PageChoice } from "@/api";

import { pageTiles, sameTile, tileName, type PageTile } from "@/lib/pageChoice";
import styles from "./PagePicker.module.css";

interface PagePickerProps {
  drawings: readonly DocumentOut[];
  /** The small drawing of one page (`?preview=true`). */
  previewUrl: (documentId: string, page: number) => string;
  chosen: PageChoice | null;
  onChoose: (tile: PageTile) => void;
  /**
   * Enter on a page: it is chosen and the way on is the button that reads
   * it — the caller puts the focus there. Two presses, both the person's;
   * nothing is read from the raster itself.
   */
  onCommit: () => void;
  /** Ties the radiogroup to the screen's question. */
  labelledBy: string;
  /**
   * The page read last („Andere Seite wählen“): marked „gelesen“ in words,
   * never chosen for the person — the next reading is a choice of its own.
   */
  readTile?: PageChoice | null;
}

/**
 * „Welche Seite zeigt den Umriss?“ — every page on file as a small picture,
 * a large target each, one choice (Seitenwahl, 13.10.2026).
 *
 * The pattern of „Lesart wählen“ (`FormUnsure`): a radiogroup, the chosen
 * page with an ink edge **and** a filled ring — never colour alone —, the
 * arrows move the choice, Tab leaves the group. Nothing is chosen on
 * opening, and focus alone chooses nothing.
 *
 * The pictures load lazily: a forty-page PDF is forty renders on a server
 * that draws one PDF page at a time. Each tile keeps its place while its
 * picture comes, and a picture that does not come leaves a tile that can be
 * chosen all the same — the page exists, only its preview does not.
 */
export function PagePicker({
  drawings,
  previewUrl,
  chosen,
  onChoose,
  onCommit,
  labelledBy,
  readTile = null,
}: PagePickerProps) {
  const tiles = pageTiles(drawings);
  const several = drawings.length > 1;
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const keyOf = (tile: Pick<PageTile, "documentId" | "page">) => `${tile.documentId}#${tile.page}`;
  const chosenIndex = tiles.findIndex((tile) => sameTile(chosen, tile));
  // Tab lands on the chosen page, or on the first while nothing is chosen.
  const tabStop = Math.max(chosenIndex, 0);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const from = tiles.findIndex((tile) => refs.current.get(keyOf(tile)) === event.target);
    if (from < 0) return;
    if (event.key === "Enter") {
      // Instead of the button's own click: chosen, and on to the button.
      event.preventDefault();
      onChoose(tiles[from]!);
      onCommit();
      return;
    }
    const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
    const backward = event.key === "ArrowLeft" || event.key === "ArrowUp";
    if (!forward && !backward && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    const count = tiles.length;
    const at = event.key === "Home" ? 0 : event.key === "End" ? count - 1 : (from + (forward ? 1 : -1) + count) % count;
    const next = tiles[at]!;
    onChoose(next);
    refs.current.get(keyOf(next))?.focus();
  }

  return (
    <div className={styles.picker} role="radiogroup" aria-labelledby={labelledBy} onKeyDown={onKeyDown}>
      {drawings.map((document) => {
        const own = tiles.filter((tile) => tile.documentId === document.id);
        return (
          <div key={document.id} className={styles.document} style={{ "--pages": own.length } as CSSProperties}>
            {several ? (
              <p className={styles.documentHead}>
                <span className={styles.documentName} title={document.originalFilename}>
                  {document.originalFilename}
                </span>
                <span className={styles.documentCount}>
                  <span className="tabular">{own.length}</span> {own.length === 1 ? "Seite" : "Seiten"}
                </span>
              </p>
            ) : null}
            <div className={styles.tiles}>
              {own.map((tile) => {
                const index = tiles.indexOf(tile);
                return (
                  <PageTileButton
                    key={keyOf(tile)}
                    tile={tile}
                    name={tileName(tile, several)}
                    src={previewUrl(tile.documentId, tile.page)}
                    checked={index === chosenIndex}
                    read={sameTile(readTile, tile)}
                    tabbable={index === tabStop}
                    buttonRef={(element) => {
                      if (element) refs.current.set(keyOf(tile), element);
                      else refs.current.delete(keyOf(tile));
                    }}
                    onChoose={() => onChoose(tile)}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

type Preview = "loading" | "loaded" | "failed";

function PageTileButton({
  tile,
  name,
  src,
  checked,
  read,
  tabbable,
  buttonRef,
  onChoose,
}: {
  tile: PageTile;
  name: string;
  src: string;
  checked: boolean;
  read: boolean;
  tabbable: boolean;
  buttonRef: (element: HTMLButtonElement | null) => void;
  onChoose: () => void;
}) {
  const [preview, setPreview] = useState<Preview>("loading");
  const missing = preview === "failed";
  const label = [name, read ? "schon gelesen" : null, missing ? "Vorschau nicht verfügbar" : null]
    .filter(Boolean)
    .join(" — ");
  return (
    <button
      ref={buttonRef}
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={label}
      tabIndex={tabbable ? 0 : -1}
      className={styles.tile}
      data-preview={preview}
      data-read={read || undefined}
      onClick={onChoose}
    >
      {/* Square, whatever the page: portrait and landscape keep one raster,
          and the place a picture takes is there before it is. */}
      {/* A page, and so a sheet (design 2026-10-24 §5.3) — the picture itself
          on the drawings' one paper tone, the square round it the desk's well
          in the mode (UX-Gutachten 2, B-10). */}
      <span className={styles.frame}>
        {missing ? (
          <span className={styles.missing}>
            Seite <span className="tabular">{tile.page}</span> — Vorschau nicht verfügbar
          </span>
        ) : (
          <img
            className={styles.image}
            src={src}
            alt=""
            data-blatt="zeichnung"
            loading="lazy"
            decoding="async"
            onLoad={() => setPreview("loaded")}
            onError={() => setPreview("failed")}
          />
        )}
      </span>
      <span className={styles.caption}>
        <span className={styles.radio} aria-hidden="true" />
        <span>
          Seite <span className="tabular">{tile.page}</span>
        </span>
        {/* A word, not a colour: the page read last is still a page to choose. */}
        {read ? (
          <span className={`label ${styles.readMark}`} aria-hidden="true">
            gelesen
          </span>
        ) : null}
      </span>
    </button>
  );
}
